import * as React from 'react';
import { observer } from 'mobx-react-lite';
import {
    getOWAConnectedAccounts,
    getAccountsStoreState,
    AccountsStoreState,
} from 'owa-accounts-store';
import { isEduStudent } from 'owa-bpos-utils';
import loc, { format } from 'owa-localize';
import {
    accountProviderSelectionInstructions,
    accountProviderSelectionInstructionsForSchool,
    accountProviderListHeader,
    connectedAccountsHeader,
    checkingAccountsLoading,
    connectedAccountSuccessHeader,
    connectedAccountSuccessHeaderEdu,
} from './AccountsEditor.locstring.json';
import AccountProvider from './AccountProvider';
import ConnectedAccount from './ConnectedAccount';
import { maxNumberConnectedAccount } from '../constants';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import type AccountProviderInfo from '../store/schema/AccountProviderInfo';
import getAccountProviders from '../utils/getAccountProviders';
import { lazyGetMergeViewEnabledAccount } from 'owa-truetime';
import type { AccountsEditorSource } from '../store/schema/AccountsEditorSource';
import setAccountsEditorSource from '../mutators/setAccountsEditorSource';
import NotificationContent from './NotificationContent';

import styles from './AccountsEditor.scss';

export interface AccountsEditorProps {
    source: AccountsEditorSource;
}

export default observer(function AccountsEditor(props: AccountsEditorProps) {
    const accountsStoreState = getAccountsStoreState();
    const accountProviders = getAccountProviders();
    const owaConnectedAccounts = getOWAConnectedAccounts();
    const showProviderSelection =
        owaConnectedAccounts.length < maxNumberConnectedAccount && accountProviders.length > 0;
    const showEditAccounts = owaConnectedAccounts.length > 0;
    const getMergeViewEnabledAccount = lazyGetMergeViewEnabledAccount.tryImportForRender();
    const mergeViewEnabledAccount = getMergeViewEnabledAccount
        ? getMergeViewEnabledAccount()
        : undefined;

    React.useEffect(() => {
        setAccountsEditorSource(props.source);
    }, [props.source]);

    return accountsStoreState == AccountsStoreState.Loading ? (
        <div className={styles.spinnerContainer}>
            <Spinner className={styles.pageLoadingSpinner} size={SpinnerSize.large} />
            <div className={styles.loadingLabel}>{loc(checkingAccountsLoading)}</div>
        </div>
    ) : (
        <div className={styles.editorContent}>
            <div className={styles.notificationContent}>
                <NotificationContent
                    connectedAccounts={owaConnectedAccounts}
                    mergeViewEnabledAccount={mergeViewEnabledAccount}
                />
            </div>
            {accountsStoreState == AccountsStoreState.AccountsLoadSuccessful && (
                <>
                    {showProviderSelection && renderProviderSelectionContent(accountProviders)}
                    {showEditAccounts && renderEditAccountsContent(mergeViewEnabledAccount)}
                </>
            )}
        </div>
    );
});

function renderEditAccountsContent(mergeViewEnabledAccount: string) {
    const owaConnectedAccounts = getOWAConnectedAccounts();

    return (
        <>
            <p className={styles.connectedAccountSuccessHeader}>
                {
                    //this string should go in map below when we have implement more than one connected account scenarios
                    format(
                        isEduStudent()
                            ? loc(connectedAccountSuccessHeaderEdu)
                            : loc(connectedAccountSuccessHeader),
                        owaConnectedAccounts[0].userIdentity
                    )
                }
            </p>
            <p className={styles.editHeader}>{loc(connectedAccountsHeader)}</p>
            {owaConnectedAccounts &&
                owaConnectedAccounts.map(account => (
                    <ConnectedAccount
                        key={account.accountId}
                        account={account}
                        mergeViewEnabledAccount={mergeViewEnabledAccount}
                    />
                ))}
        </>
    );
}

function renderProviderSelectionContent(accountProviders: AccountProviderInfo[]) {
    return (
        <div className={styles.providerSelection}>
            <div>
                <p className={styles.providerSelectionInstructions}>
                    {isEduStudent()
                        ? loc(accountProviderSelectionInstructionsForSchool)
                        : loc(accountProviderSelectionInstructions)}{' '}
                </p>
            </div>
            <div className={styles.panelContainer}>
                <div className={styles.accountProviderHeader}>{loc(accountProviderListHeader)}</div>
                {accountProviders.map(accountProvider => (
                    <AccountProvider
                        key={accountProvider.accountProviderType}
                        accountProviderInfo={accountProvider}
                    />
                ))}
            </div>
        </div>
    );
}
